import React, { useMemo, useState } from 'react';
import * as Routes from '@/modules/routes';
import gradeSyncUrl from '@/components/shared/gradeSyncUrl';
import Modal from '@/components/common/Modal';
import PaginatedTable, { usePaginatedTable } from '@/components/common/DataTable/PaginatedTable';
import AdvancedFilters from '@/components/common/AdvancedFilters';
import { buildFilterPath } from '@/components/common/AdvancedFilters/Utils';
import syncActivityColumns from './syncActivityColumns';
import syncActivityFilters from './syncActivityFilters';

interface SyncActivityProps {
  assignmentId: number;
  // Narrows the log to grade sync events. The Grades entry point wants that; the assignment-wide
  // modal on the Assignments page does not — support needs to see assignment_sync and
  // assignment_link there to diagnose a failed link (PLAT-17996's red icon).
  gradeSyncOnly?: boolean;
  isOpen: boolean;
  // Offered from the teacher's own pages, where staff arrive by masquerading; following it ends the
  // masquerade (see gradeSyncUrl).
  linkToDashboard?: boolean;
  studentId?: number;
  studentLabel?: string;
  onClose: () => void;
}

const PAGE_SIZE = 20;

// AIDEV-NOTE: Deliberately opens unfiltered. A POST default reads as the obvious choice — those are
// the score deliveries — but it hides the two kinds of row someone opening a failed sync most needs:
// Lms::LmsWorker#log_custom_event writes its reason to `raw_response` with a null `http_method`
// (student_not_linked, subscriber_disconnected, rate_limited, no_assignment), and Google Classroom
// delivers scores with PATCH. Narrowing to POST is one click away via the HTTP Method filter.
const SyncActivity = ({
  assignmentId, gradeSyncOnly = false, isOpen, linkToDashboard = false, studentId, studentLabel, onClose,
}: SyncActivityProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { columns, columnMapping, columnDefs, exportedColumns } = syncActivityColumns(isExpanded);
  const {
    resources,
    pagesLoaded,
    addResources,
    doneLoading,
    resetResources,
    updateActiveFilters,
  } = usePaginatedTable(PAGE_SIZE);

  const basePath = useMemo(() => Routes.plato_api_lms_sync_events_path({
    column: 'columnName',
    for_assignment: assignmentId,
    ...(studentId ? { for_student: studentId } : {}),
    ...(gradeSyncOnly ? { grade_sync: true } : {}),
    includes: ['student'],
    order: 'sortDirection',
    page: 'pageNumber',
    per_page: PAGE_SIZE,
  }), [assignmentId, gradeSyncOnly, studentId]);

  const [getUrl, setGetUrl] = useState(basePath);

  const kind = gradeSyncOnly ? 'Grade Sync Activity' : 'Sync Activity';
  const headerText = studentLabel
    ? `${kind} for ${studentLabel} — Assignment ID ${assignmentId}`
    : `${kind} for Assignment ID ${assignmentId}`;

  return (
    <Modal
      nearlyFullScreen
      closeModal={() => {
        setGetUrl(basePath);
        updateActiveFilters({});
        onClose();
      }}
      headerText={headerText}
      isOpen={isOpen}
    >
      {linkToDashboard && (
        <p className="tw-m-0 tw-mb-3 tw-text-sm">
          <a className="tw-text-tci-purple tw-underline" href={gradeSyncUrl(assignmentId)}>
            Diagnose this assignment in Grade Sync
          </a>
          <span className="tw-text-very-dark-grey"> — why grades aren&apos;t in the LMS, and the fix</span>
        </p>
      )}
      <AdvancedFilters
        asyncSearchPath={Routes.plato_api_search_lms_sync_events_path({
          for_assignment: assignmentId,
          ...(studentId ? { for_student: studentId } : {}),
          ...(gradeSyncOnly ? { grade_sync: true } : {}),
        })}
        properties={syncActivityFilters(!!studentId)}
        resources={resources}
        updateActiveFilters={(filters: Record<string, string>) => {
          setGetUrl(buildFilterPath(basePath, filters));
          updateActiveFilters(filters);
        }}
      />
      <PaginatedTable
        key={isExpanded}
        addResources={addResources}
        autoWidth={false}
        buttons={[
          {
            action: () => setIsExpanded(prev => !prev),
            className: 'btn btn--sm btn--outline-purple mr30',
            enabled: true,
            text: isExpanded ? 'Collapse All' : 'Expand All',
          },
        ]}
        columnDefs={columnDefs}
        columnMapping={columnMapping}
        columns={columns}
        defaultOrder={[0, 'desc']}
        doneLoading={doneLoading}
        exportedColumns={exportedColumns}
        getUrl={getUrl}
        pageSize={PAGE_SIZE}
        pagesLoaded={pagesLoaded}
        resetResources={resetResources}
        resources={resources}
      />
    </Modal>
  );
};

export default SyncActivity;
